import {
  Box,
  Card,
  CardMedia,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import LockIcon from '@mui/icons-material/Lock'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { AssignmentIcon as CustomAssignmentIcon } from 'src/v3/core/icons'
import {
  coursePublicImageSrc,
  onCourseImageError,
} from 'src/v3/self-paced/utils/course-thumbnail'
import { progressDisplayFor } from 'src/v3/self-paced/utils/progress-display'
import { CourseProgressSummary } from 'src/v3/self-paced/types'
import { StatusChip } from './status-chip'

export let CourseCard = ({
  course,
  onCourseClick,
  isAssigned,
  isLocked,
  progress,
}: {
  course: any
  onCourseClick: (id: string) => void
  isAssigned: boolean
  isLocked: boolean
  progress?: CourseProgressSummary
}) => {
  let [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  let progressDisplay = progress ? progressDisplayFor(progress) : null
  // The section sliders only ever surface two badges — Assigned and
  // Certificate ready — everything else from progressDisplayFor (in
  // progress, quiz due, survey needed, plain completed, etc.) is reserved
  // for the "Continue & action items" surfaces, where that detail belongs.
  // Showing all of it here made the regular grid too busy.
  let showCertificateBadge = progressDisplay?.tagLabel === 'Certificate ready'
  // Assigned stacks below the certificate badge, rather than overlapping it.
  let hasOtherBadge = showCertificateBadge

  let handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget as HTMLElement)
  }

  let handleMenuClose = () => {
    setMenuAnchor(null)
  }

  let handleGoToCourse = () => {
    onCourseClick(course.id)
    handleMenuClose()
  }

  return (
    <Box
      sx={{
        minWidth: 280,
        cursor: isLocked ? 'not-allowed' : 'pointer',
        transition: 'transform 0.3s ease',
        '&:hover': !isLocked
          ? {
              transform: 'scale(1.05)',
            }
          : {},
        position: 'relative',
      }}
    >
      <Card
        sx={{
          bgcolor: 'transparent',
          boxShadow: 'none',
          filter: isLocked ? 'brightness(30%)' : 'brightness(100%)',
          border: 'none',
          borderRadius: 2,
          overflow: 'hidden',
          '& .MuiCardContent-root': {
            bgcolor: '#000000',
          },
        }}
        onClick={isLocked ? undefined : () => onCourseClick(course.id)}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: course.isSeries ? '2/3' : '16/9',
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: '#000000',
          }}
        >
          <CardMedia
            component="img"
            image={
              course.isSeries
                ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${course.imageName}`
                : course.imageName
                  ? coursePublicImageSrc(course.imageName)
                  : undefined
            }
            onError={
              course.isSeries || !course.imageName
                ? undefined
                : (event) => onCourseImageError(event, course.imageName)
            }
            alt={course.displayName}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>

        {/* Status Indicators */}
        {isLocked && (
          <LockIcon
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
              p: 0.5,
            }}
          />
        )}
        {showCertificateBadge && (
          <StatusChip
            label={progressDisplay!.tagLabel}
            color={progressDisplay!.tagColor}
          />
        )}
        {isAssigned && (
          <Chip
            icon={<CustomAssignmentIcon />}
            label="Assigned"
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: hasOtherBadge ? 44 : 8,
              left: 8,
            }}
          />
        )}

        {/* Three Dot Menu - positioned over thumbnail */}
        {!isLocked && (
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'rgba(255,255,255,0.9)',
              bgcolor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.8)',
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Card>
      {/* Menu Popover */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(0,0,0,0.9)',
              color: 'white',
              minWidth: 200,
            },
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="body2">{course.displayName}</Typography>
        </MenuItem>
        {!course.isSeries && (
          <MenuItem disabled sx={{ opacity: 0.7 }}>
            <Typography variant="body2">
              {course.length
                ? `${course.length} hour${course.length === 1 ? '' : 's'}`
                : 'Duration varies'}
            </Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleGoToCourse}>
          <ListItemIcon>
            <PlayArrowIcon sx={{ color: 'white' }} />
          </ListItemIcon>
          <ListItemText
            primary={course.isSeries ? 'Go to series' : 'Go to course'}
          />
        </MenuItem>
      </Menu>
    </Box>
  )
}
